import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { HeartPulse, Pencil, Plus, Users } from 'lucide-react'
import {
  ConfirmDialog, EmployeePicker, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useBenefitEnrolments, useBenefitMutations, useBenefitPlans } from '../api'
import { COST_BASIS_LABELS, PLAN_TYPE_LABELS, RELATIONSHIP_LABELS } from '../types'
import type { BenefitPlanView, CostBasis, PlanType, Relationship } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

/**
 * Benefit plans, and who is on them.
 *
 * <p>No cost has a default anywhere on this screen. A premium is a contract with an insurer,
 * and a plausible-looking number would be charged to real people through payroll.
 */
/** A number as the form wants it: blank for nothing, never "null". */
function text(value: number | null): string {
  return value == null ? '' : String(value)
}

export function BenefitsPage() {
  useBreadcrumb()
  const plans = useBenefitPlans()
  const { savePlan, enrol, retirePlan } = useBenefitMutations()

  const [planDraft, setPlanDraft] = useState<{
    id?: string
    code: string; name: string; planType: PlanType; provider: string
    costBasis: CostBasis; employeeCost: string; employerCost: string
    costPerDependant: string; percentOfBasic: string; currency: string
    coversDependants: boolean; maxDependants: string; effectiveFrom: string
  } | null>(null)
  const [enrolFor, setEnrolFor] = useState<string | null>(null)
  const [enrolDraft, setEnrolDraft] = useState<{
    employeeId: string; effectiveFrom: string
    dependants: { fullName: string; relationship: Relationship }[]
  } | null>(null)
  const [viewing, setViewing] = useState<string | null>(null)
  const [retiring, setRetiring] = useState<BenefitPlanView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const enrolments = useBenefitEnrolments(viewing ?? undefined)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Benefits"
        subtitle="Medical, life and allowances — what each costs and who is covered."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setPlanDraft({
                code: '', name: '', planType: 'MEDICAL', provider: '',
                costBasis: 'FIXED_PER_MONTH', employeeCost: '', employerCost: '',
                costPerDependant: '', percentOfBasic: '', currency: 'AED',
                coversDependants: false, maxDependants: '',
                effectiveFrom: todayLocal(),
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New plan
          </Button>
        }
      />

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this plan'}?`}
        description="It stops being offered for new enrolments. Everybody already on it keeps their cover and their priced enrolment — this is never a delete."
        confirmLabel="Retire it"
        loading={retirePlan.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          retirePlan.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That plan could not be retired.'),
          })
        }}
      />

      <Card>
        {plans.isPending && <Skeleton className="m-4 h-24" />}
        {plans.isError && <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />}
        {plans.isSuccess && plans.data.length === 0 && (
          <EmptyState
            title="No plan is defined"
            description="A plan carries what it costs the employee and the employer. Nothing here has a default rate — a premium is a contract."
          />
        )}
        {plans.isSuccess && plans.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Plan</th>
                  <th className="p-3 text-start font-medium">Kind</th>
                  <th className="p-3 text-start font-medium">Priced</th>
                  <th className="p-3 text-end font-medium">Employee</th>
                  <th className="p-3 text-end font-medium">Employer</th>
                  <th className="p-3 text-start font-medium">Dependants</th>
                  <th className="p-3 text-end font-medium">Enrolled</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {plans.data.map((plan) => (
                  <tr key={plan.id} className={`border-b border-slate-100 ${plan.active ? '' : 'text-slate-400'}`}>
                    <td className="p-3">
                      {plan.name}
                      <span className="block font-mono text-xs text-slate-500">{plan.code}</span>
                    </td>
                    <td className="p-3 text-slate-600">
                      {PLAN_TYPE_LABELS[plan.planType as PlanType] ?? plan.planType}
                    </td>
                    <td className="p-3 text-slate-600">
                      {COST_BASIS_LABELS[plan.costBasis as CostBasis] ?? plan.costBasis}
                    </td>
                    <td className="p-3 text-end">
                      {plan.employeeCost ?? <span className="text-amber-700">not priced</span>}
                    </td>
                    <td className="p-3 text-end">{plan.employerCost ?? '—'}</td>
                    <td className="p-3 text-slate-600">
                      {plan.coversDependants
                        ? `up to ${plan.maxDependants ?? 'any number'}`
                        : 'not covered'}
                    </td>
                    <td className="p-3 text-end">{plan.enrolledCount}</td>
                    <td className="p-3 text-end">
                      <Button
                        type="button" variant="ghost" size="sm"
                        aria-label={`Edit ${plan.name}`}
                        onClick={() => {
                          setError(null)
                          setPlanDraft({
                            id: plan.id,
                            code: plan.code,
                            name: plan.name,
                            planType: plan.planType as PlanType,
                            provider: plan.provider ?? '',
                            costBasis: plan.costBasis as CostBasis,
                            employeeCost: text(plan.employeeCost),
                            employerCost: text(plan.employerCost),
                            costPerDependant: text(plan.costPerDependant),
                            percentOfBasic: text(plan.percentOfBasic),
                            currency: plan.currency ?? '',
                            coversDependants: plan.coversDependants,
                            maxDependants: text(plan.maxDependants),
                            effectiveFrom: plan.effectiveFrom,
                          })
                        }}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                      {plan.active && (
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => { setError(null); setRetiring(plan) }}
                        >
                          Retire
                        </Button>
                      )}
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => {
                          setError(null)
                          setEnrolFor(plan.id)
                          setEnrolDraft({
                            employeeId: '',
                            effectiveFrom: todayLocal(),
                            dependants: [],
                          })
                        }}
                      >
                        <Users className="size-4" aria-hidden="true" />
                        Enrol
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="mb-2 flex items-center gap-2 text-base font-semibold">
          <HeartPulse className="size-4" aria-hidden="true" /> Somebody&rsquo;s enrolments
        </h2>
        <div className="mb-3 max-w-md">
          <Label htmlFor="benefit-employee">Person</Label>
          <EmployeePicker
                  id="benefit-employee"
                  value={viewing ?? ''}
                  onChange={(employeeId) => setViewing(employeeId)}
                />
        </div>
        {enrolments.isSuccess && enrolments.data.length === 0 && (
          <p className="text-sm text-slate-600">They are not enrolled on anything.</p>
        )}
        {enrolments.data && enrolments.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-2 text-start font-medium">Plan</th>
                <th className="p-2 text-start font-medium">From</th>
                <th className="p-2 text-end font-medium">They pay</th>
                <th className="p-2 text-end font-medium">Employer pays</th>
                <th className="p-2 text-start font-medium">Dependants</th>
                <th className="p-2 text-start font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {enrolments.data.map((enrolment) => (
                <tr key={enrolment.id} className="border-b border-slate-100">
                  <td className="p-2">{enrolment.planName}</td>
                  <td className="p-2">{enrolment.effectiveFrom}</td>
                  <td className="p-2 text-end">{enrolment.employeeCost ?? '—'}</td>
                  <td className="p-2 text-end">{enrolment.employerCost ?? '—'}</td>
                  <td className="p-2 text-slate-600">
                    {enrolment.dependants.map((d) => d.fullName).join(', ') || '—'}
                  </td>
                  <td className="p-2"><StatusBadge status={enrolment.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-2 text-xs text-slate-600">
          The cost is what was worked out when they were enrolled. The plan&rsquo;s price moves
          at renewal; the enrolment keeps saying what this person was actually charged.
        </p>
      </Card>

      <FormDialog
        open={planDraft != null}
        onOpenChange={(open) => { if (!open) setPlanDraft(null) }}
        size="lg"
        title={planDraft?.id ? 'Edit benefit plan' : 'New benefit plan'}
        submitLabel="Create the plan"
        description="Nothing here has a default. A premium is a contract with an insurer."
        loading={savePlan.isPending}
        error={error}
        onSubmit={() => {
          if (!planDraft) return
          void (async () => {
            setError(null)
            try {
              await savePlan.mutateAsync({
                id: planDraft.id,
                code: planDraft.code,
                name: planDraft.name,
                planType: planDraft.planType,
                provider: planDraft.provider || null,
                costBasis: planDraft.costBasis,
                employeeCost: planDraft.employeeCost ? Number(planDraft.employeeCost) : null,
                employerCost: planDraft.employerCost ? Number(planDraft.employerCost) : null,
                costPerDependant: planDraft.costPerDependant
                  ? Number(planDraft.costPerDependant) : null,
                percentOfBasic: planDraft.percentOfBasic ? Number(planDraft.percentOfBasic) : null,
                currency: planDraft.currency,
                coversDependants: planDraft.coversDependants,
                maxDependants: planDraft.maxDependants ? Number(planDraft.maxDependants) : null,
                effectiveFrom: planDraft.effectiveFrom,
              })
              setPlanDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That plan could not be saved')
            }
          })()
        }}
      >
        {planDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="plan-code">Code</Label>
                <Input
                  id="plan-code" className="font-mono" value={planDraft.code}
                  onChange={(event) =>
                    setPlanDraft({ ...planDraft, code: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="plan-name">Name</Label>
                <Input
                  id="plan-name" value={planDraft.name}
                  onChange={(event) => setPlanDraft({ ...planDraft, name: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="plan-type">Kind</Label>
                <Select
                  id="plan-type"
                  className="w-full"
                  value={planDraft.planType}
                  onChange={(event) =>
                    setPlanDraft({ ...planDraft, planType: event.target.value as PlanType })}
                >
                  {Object.entries(PLAN_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="plan-basis">How it is priced</Label>
                <Select
                  id="plan-basis"
                  className="w-full"
                  value={planDraft.costBasis}
                  onChange={(event) =>
                    setPlanDraft({ ...planDraft, costBasis: event.target.value as CostBasis })}
                >
                  {Object.entries(COST_BASIS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="plan-employee-cost">Employee pays</Label>
                <Input
                  id="plan-employee-cost" type="number" value={planDraft.employeeCost}
                  onChange={(event) =>
                    setPlanDraft({ ...planDraft, employeeCost: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="plan-employer-cost">Employer pays</Label>
                <Input
                  id="plan-employer-cost" type="number" value={planDraft.employerCost}
                  onChange={(event) =>
                    setPlanDraft({ ...planDraft, employerCost: event.target.value })}
                />
              </div>
              {planDraft.costBasis === 'PER_DEPENDANT' && (
                <div>
                  <Label htmlFor="plan-dep-cost">Each dependant adds</Label>
                  <Input
                    id="plan-dep-cost" type="number" value={planDraft.costPerDependant}
                    onChange={(event) =>
                      setPlanDraft({ ...planDraft, costPerDependant: event.target.value })}
                  />
                </div>
              )}
              {planDraft.costBasis === 'PERCENT_OF_BASIC' && (
                <div>
                  <Label htmlFor="plan-percent">Percentage of basic</Label>
                  <Input
                    id="plan-percent" type="number" value={planDraft.percentOfBasic}
                    onChange={(event) =>
                      setPlanDraft({ ...planDraft, percentOfBasic: event.target.value })}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="plan-from">Effective from</Label>
                <DatePicker
                  id="plan-from" value={planDraft.effectiveFrom}
                  onChange={(event) =>
                    setPlanDraft({ ...planDraft, effectiveFrom: event.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4"
                checked={planDraft.coversDependants}
                onChange={(event) =>
                  setPlanDraft({ ...planDraft, coversDependants: event.target.checked })}
              />
              Covers dependants
            </label>
            {planDraft.coversDependants && (
              <div className="max-w-xs">
                <Label htmlFor="plan-max-dep">At most how many</Label>
                <Input
                  id="plan-max-dep" type="number" value={planDraft.maxDependants}
                  onChange={(event) =>
                    setPlanDraft({ ...planDraft, maxDependants: event.target.value })}
                />
              </div>
            )}
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={enrolDraft != null}
        onOpenChange={(open) => { if (!open) { setEnrolDraft(null); setEnrolFor(null) } }}
        title="Enrol somebody"
        description="The cost is worked out now and kept on the enrolment, so a renewal does not reprice the past."
        submitLabel="Enrol"
        loading={enrol.isPending}
        error={error}
        onSubmit={() => {
          if (!enrolDraft || !enrolFor) return
          void (async () => {
            setError(null)
            try {
              await enrol.mutateAsync({
                employeeId: enrolDraft.employeeId,
                planId: enrolFor,
                effectiveFrom: enrolDraft.effectiveFrom,
                dependants: enrolDraft.dependants,
              })
              setEnrolDraft(null)
              setEnrolFor(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'They could not be enrolled')
            }
          })()
        }}
      >
        {enrolDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="enrol-employee">Person</Label>
              <EmployeePicker
                  id="enrol-employee"
                  value={enrolDraft.employeeId}
                  onChange={(employeeId) => setEnrolDraft({ ...enrolDraft, employeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="enrol-from">From</Label>
              <DatePicker
                id="enrol-from" value={enrolDraft.effectiveFrom}
                onChange={(event) =>
                  setEnrolDraft({ ...enrolDraft, effectiveFrom: event.target.value })}
              />
            </div>
            <div>
              <span className="text-sm font-medium">Dependants</span>
              {enrolDraft.dependants.map((dependant, index) => (
                <div key={index} className="mt-2 flex gap-2">
                  <Input
                    aria-label={`Dependant ${index + 1} name`}
                    value={dependant.fullName}
                    onChange={(event) => {
                      const next = [...enrolDraft.dependants]
                      next[index] = { ...dependant, fullName: event.target.value }
                      setEnrolDraft({ ...enrolDraft, dependants: next })
                    }}
                  />
                  <Select
                    aria-label={`Dependant ${index + 1} relationship`}
                    value={dependant.relationship}
                    onChange={(event) => {
                      const next = [...enrolDraft.dependants]
                      next[index] = {
                        ...dependant, relationship: event.target.value as Relationship }
                      setEnrolDraft({ ...enrolDraft, dependants: next })
                    }}
                  >
                    {Object.entries(RELATIONSHIP_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
              ))}
              <Button
                type="button" variant="outline" size="sm" className="mt-2"
                onClick={() => setEnrolDraft({
                  ...enrolDraft,
                  dependants: [...enrolDraft.dependants,
                    { fullName: '', relationship: 'CHILD' }],
                })}
              >
                <Plus className="size-4" aria-hidden="true" />
                Add a dependant
              </Button>
              <p className="mt-1 text-xs text-slate-600">
                Only a name and a relationship. This is here to price the policy and say who is
                on it — a dependant&rsquo;s identity documents are more data than that needs.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
